import uFuzzy from '@leeoniya/ufuzzy';
import { HighlightRangePairs } from './render_search';

export type FuzzyMatch<T> = Readonly<{
    item: T;
    /** highlight offset ranges: [start0, end0, start1, end1, ...] */
    ranges: HighlightRangePairs;
}>;

export type FuzzySearch<T> = Readonly<{
    search(query: string): FuzzyMatch<T>[];
}>;

export type FuzzyOptions = uFuzzy.Options;

const FUZZY_OPTS: FuzzyOptions = { intraMode: 1, intraIns: 1 };

/**
 * Creates a fuzzy searcher usable for filtering an array of `items` based on
 * each item's text `key`. Results are sorted by match quality and include
 * highlight ranges.
 */
export function createFuzzySearch<T>(
    items: readonly T[],
    key: (item: T) => string,
    opts: FuzzyOptions = FUZZY_OPTS,
): FuzzySearch<T> {
    const uf = new uFuzzy(opts);
    const haystack = items.map(key);
    let prevQuery: string | null = null;
    let prevIdxs: number[] = [];

    return {
        search(query) {
            if (!query) {
                prevQuery = null;
                prevIdxs = [];
                return [];
            }

            const narrow =
                prevQuery !== null && query.startsWith(prevQuery) ? prevIdxs : undefined;

            const idxs = uf.filter(haystack, query, narrow) ?? [];
            prevQuery = query;
            prevIdxs = idxs;

            if (idxs.length === 0) {
                return [];
            }

            const info = uf.info(idxs, haystack, query);
            const order = uf.sort(info, haystack, query);

            return order.map((oi) => ({
                item: items[info.idx[oi]],
                ranges: info.ranges[oi] as HighlightRangePairs, // branded type
            }));
        },
    };
}
